/**
 * Settings — Screen 8
 * Tabs: AI Agent · Team · Compliance · Integrations · Tags & fields · Notifications
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchSettings, patchSettings,
  fetchTeam, patchTeamMember,
  fetchTcpa, overrideTcpa,
  type WorkspaceSettings,
} from '../../lib/apiHelpers.js';
import { AiOrb } from '../../components/AiOrb.js';
import { Toggle } from '../../components/Toggle.js';
import { ComingSoon } from '../../components/ComingSoon.js';
import type { ApiTeamMember, ApiTcpaRule } from '@contactrate/shared';
import { ManagedPill, ViewOnlyRibbon } from '../../components/ManagedChrome.js';

type SettingsTab = 'agent' | 'team' | 'compliance' | 'integrations' | 'tags' | 'notifications';

const TABS: { key: SettingsTab; label: string }[] = [
  { key: 'agent', label: 'AI Agent' },
  { key: 'team', label: 'Team' },
  { key: 'compliance', label: 'Compliance' },
  { key: 'integrations', label: 'Integrations' },
  { key: 'tags', label: 'Tags & fields' },
  { key: 'notifications', label: 'Notifications' },
];

// ── List manager (Need to know / Nice to know / DQ) ──────────────────────────

function ListManager({
  title,
  color,
  items,
  onChange,
}: {
  title: string;
  color: string;
  items: string[];
  onChange: (items: string[]) => void;
}) {
  const [input, setInput] = useState('');

  const add = () => {
    if (!input.trim()) return;
    onChange([...items, input.trim()]);
    setInput('');
  };

  const remove = (i: number) => {
    onChange(items.filter((_, idx) => idx !== i));
  };

  return (
    <div className="bg-surface rounded-card shadow-card p-4 flex flex-col gap-3">
      <p
        className="text-[12.5px] font-semibold"
        style={{ color }}
      >
        {title}
      </p>
      <div className="flex flex-col gap-1.5">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-2">
            <span
              className="w-1.5 h-1.5 rounded-full flex-shrink-0"
              style={{ background: color }}
            />
            <span className="text-[12.5px] text-ink flex-1">{item}</span>
            <button
              onClick={() => remove(i)}
              className="text-muted hover:text-red-DEFAULT transition-colors text-base"
            >
              ×
            </button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && add()}
          placeholder="Add item…"
          className="flex-1 text-[12.5px] px-3 py-1.5 rounded-[8px] bg-surface-alt border border-border outline-none focus:border-blue-DEFAULT text-ink"
        />
        <button
          onClick={add}
          className="px-3 py-1.5 rounded-[8px] text-[11.5px] font-semibold transition-colors"
          style={{ background: `${color}18`, color }}
        >
          Add
        </button>
      </div>
    </div>
  );
}

// ── AI Agent tab ──────────────────────────────────────────────────────────────

function AgentTab({ aiName, onAiNameChange }: { aiName: string; onAiNameChange: (n: string) => void }) {
  // managed theater: brain lists are visible but not the client's factory controls
  const qc = useQueryClient();
  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: fetchSettings });

  const [nameInput, setNameInput] = useState(aiName);
  const [goal, setGoal] = useState<'transfer' | 'book' | 'close'>('transfer');

  useEffect(() => {
    if (settings) {
      setGoal(settings.defaultGoal as 'transfer' | 'book' | 'close');
      setNameInput(settings.aiName);
    }
  }, [settings]);

  const patch = useMutation({
    mutationFn: patchSettings,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings'] }),
  });

  const mustKnow = settings?.settings?.must_know ?? [];
  const niceToKnow = settings?.settings?.nice_to_know ?? [];
  const hardDq = settings?.settings?.hard_dq ?? [];

  const handleNameBlur = () => {
    if (nameInput !== aiName && nameInput.trim()) {
      patch.mutate({ ai_name: nameInput.trim() });
      onAiNameChange(nameInput.trim());
    }
  };

  const GOALS: { key: 'transfer' | 'book' | 'close'; label: string }[] = [
    { key: 'transfer', label: 'Live transfer first' },
    { key: 'book', label: 'Book appointments' },
    { key: 'close', label: 'Close sales' },
  ];

  return (
    <div className="flex flex-col gap-6 max-w-2xl">
      <ViewOnlyRibbon
        title="Ava’s brain is managed by ContactRate"
        detail="You can see the playbook. We own need-to-know, DQ, and goal — white-labeled as Ava from your office."
      />
      {/* Identity card */}
      <div className="bg-surface rounded-card shadow-card p-5">
        <div className="flex items-start gap-4 mb-4">
          <AiOrb size={48} />
          <div className="flex-1">
            <div className="mb-2"><ManagedPill>Ava · your office</ManagedPill></div>
            <input
              type="text"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onBlur={handleNameBlur}
              readOnly
              className="text-[18px] font-bold text-ink bg-transparent border-0 outline-none border-b-2 border-transparent focus:border-blue-DEFAULT transition-colors mb-1 w-full"
              style={{ fontFamily: '"Plus Jakarta Sans", system-ui' }}
              placeholder="AI agent name"
            />
            <p className="text-[11.5px] text-muted">
              Warm, mid-30s female voice · Tuned and managed by your DialFuel team
            </p>
            <button className="mt-1.5 text-[11.5px] font-medium text-blue-DEFAULT hover:text-blue-hover transition-colors">
              Preview voice →
            </button>
          </div>
        </div>

        {/* Training signal */}
        <div
          className="flex items-center gap-4 px-3 py-2.5 rounded-[10px]"
          style={{ background: '#FAFBFC', border: '1px solid #F0F3F8' }}
        >
          <span className="text-[11.5px] font-medium text-green-DEFAULT flex items-center gap-1">
            <span>▲</span> 92
          </span>
          <span className="text-[11.5px] font-medium text-red-DEFAULT flex items-center gap-1">
            <span>▼</span> 7
          </span>
          <span className="text-[11px] text-muted flex-1">
            · 12 behaviors updated this month
          </span>
        </div>
      </div>

      {/* Default goal */}
      <div className="bg-surface rounded-card shadow-card p-4">
        <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted mb-3">
          Default goal
        </p>
        <div
          className="flex rounded-[10px] overflow-hidden"
          style={{ border: '1px solid #E5EAF2', background: '#FAFBFC', display: 'inline-flex' }}
        >
          {GOALS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => {
                setGoal(key);
                patch.mutate({ default_goal: key });
              }}
              className="px-4 py-2 text-[12px] font-medium transition-colors"
              style={
                goal === key
                  ? { background: '#0D1729', color: 'white' }
                  : { color: '#64748B' }
              }
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* List managers */}
      <ListManager
        title="Need to know"
        color="#0D9488"
        items={mustKnow}
        onChange={(items) => patch.mutate({ must_know: items })}
      />
      <ListManager
        title="Nice to know"
        color="#2E66E0"
        items={niceToKnow}
        onChange={(items) => patch.mutate({ nice_to_know: items })}
      />
      <ListManager
        title="Hard disqualifiers"
        color="#EF4444"
        items={hardDq}
        onChange={(items) => patch.mutate({ hard_dq: items })}
      />

      {/* Footnote */}
      <p className="text-[11.5px] text-muted flex items-start gap-1">
        <span className="text-violet-DEFAULT flex-shrink-0">✦</span>
        That's everything {aiName} needs from you. Scripts, objection handling, and tone are trained by DialFuel — your thumbs up/down in the Inbox steer her from there.
      </p>
    </div>
  );
}

// ── Team tab ──────────────────────────────────────────────────────────────────

function TeamTab() {
  const { data: team } = useQuery({ queryKey: ['team'], queryFn: fetchTeam });
  const qc = useQueryClient();

  const patch = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<ApiTeamMember> }) =>
      patchTeamMember(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team'] }),
  });

  const members = team ?? [];

  return (
    <div className="flex flex-col gap-4 max-w-2xl">
      <div className="flex items-center justify-between">
        <p className="text-[13.5px] font-semibold text-ink">
          {members.length} closers on your team
        </p>
        <button
          className="px-3 py-1.5 rounded-btn text-[11.5px] font-semibold text-white"
          style={{ background: '#2E66E0' }}
        >
          + Add teammate
        </button>
      </div>

      {members.map((m) => (
        <div key={m.id} className="bg-surface rounded-card shadow-card p-4">
          <div className="flex items-start gap-3">
            {/* Avatar */}
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-[14px] font-semibold text-white flex-shrink-0"
              style={{ background: '#2E66E0' }}
            >
              {m.name.split(' ').map((p) => p[0]).join('').toUpperCase().slice(0, 2)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <p className="text-[13.5px] font-semibold text-ink">{m.name}</p>
                <span className="text-[10.5px] text-muted">{m.role}</span>
                {m.available && (
                  <span
                    className="px-1.5 py-0.5 rounded-chip text-[9px] font-semibold"
                    style={{ background: 'rgba(21,150,98,0.1)', color: '#159662' }}
                  >
                    Available now
                  </span>
                )}
              </div>
              <p
                className="text-[11.5px] text-muted"
                style={{ fontFamily: '"DM Mono", monospace' }}
              >
                {m.phone ?? '—'}
              </p>
              <p className="text-[11.5px] text-muted mt-0.5">
                Receives:{' '}
                <span className="font-medium text-ink capitalize">
                  {m.receives.replace(/_/g, ' ')}
                </span>
              </p>
            </div>
            <div className="flex-shrink-0 flex items-center gap-2">
              <span className="text-[11px] text-muted">Available</span>
              <Toggle
                checked={m.available}
                onChange={(v) => patch.mutate({ id: m.id, body: { available: v } })}
                size="sm"
                trackOn="#159662"
                trackOff="#D8DFEA"
              />
            </div>
          </div>
        </div>
      ))}

      {members.length === 0 && (
        <div className="text-center py-8 text-muted text-[12.5px]">
          No team members yet. Add your first closer to get started.
        </div>
      )}
    </div>
  );
}

// ── Compliance tab ────────────────────────────────────────────────────────────

function ComplianceTab() {
  const { data: rules } = useQuery({ queryKey: ['tcpa'], queryFn: fetchTcpa });
  const qc = useQueryClient();
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [confirmReason, setConfirmReason] = useState('');

  const override = useMutation({
    mutationFn: ({ id, enforced, reason }: { id: string; enforced: boolean; reason: string }) =>
      overrideTcpa(id, enforced, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tcpa'] });
      setConfirmId(null);
      setConfirmReason('');
    },
  });

  const tcpaRules = rules ?? DEMO_TCPA;

  return (
    <div className="flex flex-col gap-5 max-w-2xl">
      <ViewOnlyRibbon
        title="Compliance is ContactRate’s job"
        detail="You generate trust by seeing the rules. You never own the numbers, overrides, or consent stack."
      />
      {/* Shield intro */}
      <div
        className="flex items-start gap-3 p-4 rounded-card"
        style={{ background: 'rgba(109,74,234,0.04)', border: '1px solid rgba(109,74,234,0.12)' }}
      >
        <span className="text-[20px] flex-shrink-0">🛡️</span>
        <div>
          <p className="text-[13px] font-semibold text-ink mb-1">TCPA quiet hours enforced</p>
          <p className="text-[12px] text-muted">
            Quiet hours are enforced everywhere, in the lead's local time. Ava checks before every call or text.
            Overrides stay with ContactRate ops — not the client portal.
          </p>
        </div>
      </div>

      {/* TCPA table */}
      <div className="bg-surface rounded-card shadow-card overflow-hidden">
        <table className="w-full">
          <thead>
            <tr style={{ borderBottom: '1px solid #F0F3F8' }}>
              {['Region', 'Allowed window', 'Basis', 'Note', 'Status'].map((col) => (
                <th
                  key={col}
                  className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-muted"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tcpaRules.map((rule) => {
              const isOverriding = confirmId === rule.id;
              const overridden = !rule.enforced && !rule.locked;

              return (
                <React.Fragment key={rule.id}>
                  <tr
                    style={{
                      borderBottom: '1px solid #F0F3F8',
                      background: overridden ? 'rgba(239,68,68,0.03)' : undefined,
                    }}
                  >
                    <td className="px-4 py-2.5 text-[12.5px] font-medium text-ink">
                      <div className="flex items-center gap-1.5">
                        {rule.locked && <span className="text-[12px]">🔒</span>}
                        {rule.region}
                      </div>
                    </td>
                    <td
                      className="px-4 py-2.5 text-[12px]"
                      style={{
                        fontFamily: '"DM Mono", monospace',
                        color: overridden ? '#EF4444' : '#0D1729',
                      }}
                    >
                      {overridden ? 'Unrestricted' : `${rule.windowStart} – ${rule.windowEnd}`}
                    </td>
                    <td className="px-4 py-2.5 text-[11.5px] text-muted">{rule.basis}</td>
                    <td className="px-4 py-2.5 text-[11px] text-muted max-w-[180px] truncate">
                      {rule.note ?? '—'}
                    </td>
                    <td className="px-4 py-2.5">
                      {rule.locked ? (
                        <span
                          className="px-2 py-0.5 rounded-chip text-[10px] font-semibold"
                          style={{ background: 'rgba(21,150,98,0.1)', color: '#159662' }}
                        >
                          Enforced (federal)
                        </span>
                      ) : overridden ? (
                        <button
                          onClick={() => {
                            override.mutate({ id: rule.id, enforced: true, reason: 'Re-enforced by user' });
                          }}
                          className="px-2 py-0.5 rounded-chip text-[10px] font-semibold"
                          style={{ background: 'rgba(239,68,68,0.1)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.3)' }}
                        >
                          ⚠ Overridden — click to re-enforce
                        </button>
                      ) : (
                        <button
                          onClick={() => setConfirmId(rule.id)}
                          className="px-2 py-0.5 rounded-chip text-[10px] font-semibold transition-colors hover:opacity-80"
                          style={{ background: 'rgba(21,150,98,0.1)', color: '#159662', border: '1px solid rgba(21,150,98,0.25)' }}
                        >
                          Enforced
                        </button>
                      )}
                    </td>
                  </tr>

                  {/* Override confirm card */}
                  {isOverriding && (
                    <tr>
                      <td colSpan={5} className="px-4 py-3">
                        <div
                          className="p-4 rounded-[10px] flex flex-col gap-3"
                          style={{ background: 'rgba(239,68,68,0.04)', border: '1px solid rgba(239,68,68,0.25)' }}
                        >
                          <p className="text-[12.5px] font-semibold" style={{ color: '#DC2626' }}>
                            Remove protection for {rule.region}?
                          </p>
                          <p className="text-[12px] text-muted">
                            TCPA penalties run <strong>$500–$1,500 per call or text</strong> and land on your
                            business. This override is logged and visible to your operator.
                          </p>
                          <textarea
                            value={confirmReason}
                            onChange={(e) => setConfirmReason(e.target.value)}
                            placeholder="Reason for override (required)…"
                            className="w-full text-[12px] px-3 py-2 rounded-[8px] border border-border outline-none resize-none focus:border-blue-DEFAULT"
                            rows={2}
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                if (confirmReason.trim().length < 10) return;
                                override.mutate({ id: rule.id, enforced: false, reason: confirmReason.trim() });
                              }}
                              disabled={confirmReason.trim().length < 10}
                              className="px-3 py-1.5 rounded-btn text-[11.5px] font-semibold text-white transition-colors disabled:opacity-40"
                              style={{ background: '#DC2626' }}
                            >
                              I understand — remove protection
                            </button>
                            <button
                              onClick={() => { setConfirmId(null); setConfirmReason(''); }}
                              className="px-3 py-1.5 rounded-btn text-[11.5px] font-medium text-ink"
                              style={{ background: '#F2F4F7' }}
                            >
                              Keep enforced
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer checks */}
      <div className="flex flex-col gap-2 px-1">
        {[
          'DNC list: 214 suppressed',
          'Consent stored per lead',
          'A2P 10DLC registered',
        ].map((item) => (
          <div key={item} className="flex items-center gap-2">
            <div
              className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(21,150,98,0.1)' }}
            >
              <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#159662" strokeWidth="3">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <span className="text-[12px] text-muted">{item}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const DEMO_TCPA: ApiTcpaRule[] = [
  { id: 'tcpa_federal', region: 'Federal', windowStart: '08:00', windowEnd: '21:00', basis: 'TCPA 47 U.S.C. § 227', note: 'In lead local time', enforced: true, locked: true, overrideLog: [] },
  { id: 'tcpa_fl', region: 'Florida', windowStart: '08:00', windowEnd: '21:00', basis: 'Fla. Stat. § 501.059', note: null, enforced: true, locked: false, overrideLog: [] },
  { id: 'tcpa_tx', region: 'Texas', windowStart: '09:00', windowEnd: '21:00', basis: 'Tex. Bus. & Com. § 305', note: null, enforced: true, locked: false, overrideLog: [] },
  { id: 'tcpa_ca', region: 'California', windowStart: '08:00', windowEnd: '21:00', basis: 'Cal. Pub. Util. § 2871', note: 'Stricter opt-out rules', enforced: true, locked: false, overrideLog: [] },
];

// ── Integrations tab ──────────────────────────────────────────────────────────

function IntegrationsTab() {
  const CARDS = [
    { name: 'Lead Webhook', status: 'connected', icon: '🔗', note: 'contactrate.io/webhooks/mpq-a42b', action: 'Copy URL' },
    { name: 'Facebook Lead Ads', status: 'connected', icon: '📘', note: 'MPQ FB Page connected', action: 'Manage' },
    { name: 'Google Calendar', status: 'connected', icon: '📅', note: 'dan@mortgageprotectionquotes.com', action: 'Manage' },
    { name: 'Phone & SMS', status: 'connected', icon: '📱', note: '+1 (512) 555-0100 · A2P approved', action: 'Manage' },
    { name: 'GoHighLevel', status: 'attention', icon: '⚡', note: 'Token expired — reconnect needed', action: 'Reconnect' },
    { name: 'Zapier', status: 'not_connected', icon: '⚙️', note: 'Connect to automate workflows', action: 'Connect' },
    { name: 'Cal.com', status: 'not_connected', icon: '🗓️', note: 'Sync availability', action: 'Connect' },
  ];

  const statusConfig: Record<string, { color: string; bg: string; label: string }> = {
    connected: { color: '#159662', bg: 'rgba(21,150,98,0.1)', label: 'Connected' },
    attention: { color: '#D97706', bg: 'rgba(217,119,6,0.1)', label: 'Needs attention' },
    not_connected: { color: '#64748B', bg: '#F2F4F7', label: 'Not connected' },
  };

  return (
    <div className="grid gap-4 max-w-2xl" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
      {CARDS.map((card) => {
        const sc = statusConfig[card.status];
        const needsCopy = card.action === 'Copy URL';

        return (
          <div key={card.name} className="bg-surface rounded-card shadow-card p-4 flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <span className="text-[20px]">{card.icon}</span>
              <div className="flex-1">
                <p className="text-[13px] font-semibold text-ink">{card.name}</p>
                <p className="text-[11px] text-muted truncate">{card.note}</p>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span
                className="px-2 py-0.5 rounded-chip text-[10px] font-semibold"
                style={{ background: sc.bg, color: sc.color }}
              >
                {sc.label}
              </span>
              <button
                className="px-2.5 py-1 rounded-[8px] text-[11px] font-medium transition-colors"
                style={
                  card.status === 'attention'
                    ? { background: 'rgba(217,119,6,0.1)', color: '#D97706' }
                    : { background: 'rgba(46,102,224,0.08)', color: '#2E66E0' }
                }
              >
                {card.action}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Tags & Fields tab ─────────────────────────────────────────────────────────

function TagsTab() {
  const DEMO_TAGS = [
    { name: 'hot', count: 8 },
    { name: 'callback', count: 4 },
    { name: 'spouse-included', count: 12 },
    { name: 'high-value', count: 6 },
  ];

  const DEMO_FIELDS = [
    { label: 'Age band', key: 'age_band', source: 'Webhook · form field', type: 'text' },
    { label: 'Mortgage balance', key: 'mortgage_balance', source: 'Webhook · form field', type: 'text' },
    { label: 'Beneficiary', key: 'beneficiary', source: 'Webhook · form field', type: 'text' },
    { label: 'TCPA consent', key: 'consent', source: 'Auto-captured', type: 'consent' },
  ];

  return (
    <div className="flex flex-col gap-6 max-w-2xl">
      {/* Tags */}
      <div className="bg-surface rounded-card shadow-card p-4">
        <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted mb-3">Tags</p>
        <div className="flex flex-col gap-2">
          {DEMO_TAGS.map((tag) => (
            <div key={tag.name} className="flex items-center gap-2">
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-chip text-[11px] font-medium"
                style={{ background: '#F2F4F7', color: '#64748B', border: '1px solid #E5EAF2' }}
              >
                {tag.name}
              </span>
              <span
                className="text-[10.5px] text-muted"
                style={{ fontFamily: '"DM Mono", monospace' }}
              >
                {tag.count} leads
              </span>
              <div className="flex-1" />
              <button className="text-muted hover:text-red-DEFAULT text-base transition-colors">×</button>
            </div>
          ))}
          <div className="flex gap-2 mt-1">
            <input
              type="text"
              placeholder="New tag…"
              className="flex-1 text-[12px] px-3 py-1.5 rounded-[8px] bg-surface-alt border border-border outline-none focus:border-blue-DEFAULT text-ink"
            />
            <button
              className="px-3 py-1.5 rounded-[8px] text-[11.5px] font-medium"
              style={{ background: 'rgba(46,102,224,0.08)', color: '#2E66E0' }}
            >
              Add
            </button>
          </div>
        </div>
        <p className="text-[11px] text-muted mt-2 flex items-start gap-1">
          <span className="text-violet-DEFAULT">✦</span>
          Ava can apply these tags from conversation context.
        </p>
      </div>

      {/* Custom fields */}
      <div className="bg-surface rounded-card shadow-card p-4">
        <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted mb-3">Custom fields</p>
        <div className="flex flex-col gap-2">
          {DEMO_FIELDS.map((field) => (
            <div key={field.key} className="flex items-center gap-3 py-1.5 border-b border-hairline last:border-0">
              <div className="flex-1">
                <p className="text-[12.5px] font-medium text-ink">{field.label}</p>
                <p className="text-[11px] text-muted">{field.source}</p>
              </div>
              <span
                className="px-1.5 py-0.5 rounded-chip text-[9.5px] font-medium"
                style={{ fontFamily: '"DM Mono", monospace', background: '#F2F4F7', color: '#64748B' }}
              >
                {field.key}
              </span>
              <span
                className="px-2 py-0.5 rounded-chip text-[10px] font-medium"
                style={{ background: 'rgba(46,102,224,0.08)', color: '#2E66E0' }}
              >
                {field.type}
              </span>
              <button className="text-muted hover:text-red-DEFAULT text-base transition-colors">×</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Notifications tab ─────────────────────────────────────────────────────────

function NotificationsTab() {
  const qc = useQueryClient();
  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: fetchSettings });
  const notifs = settings?.settings?.notifications ?? {};

  const patch = useMutation({
    mutationFn: (notifications: Record<string, boolean>) =>
      patchSettings({ notifications }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings'] }),
  });

  const ROWS = [
    { key: 'live_transfer', label: 'Live transfer incoming', sub: 'Ring your cell when Ava requests a transfer' },
    { key: 'appt_booked', label: 'Appointment booked', sub: 'Push when Ava books a new appointment' },
    { key: 'ai_needs_you', label: '«AI» needs you', sub: 'When Ava flags an out-of-script situation' },
    { key: 'daily_digest', label: 'Daily digest', sub: 'End-of-day summary of contacts, transfers, bookings' },
  ];

  return (
    <div className="flex flex-col gap-3 max-w-xl">
      {ROWS.map(({ key, label, sub }) => {
        const enabled = notifs[key] !== false; // default on

        return (
          <div
            key={key}
            className="bg-surface rounded-card shadow-card px-4 py-3.5 flex items-center gap-4"
          >
            <div className="flex-1">
              <p className="text-[13px] font-semibold text-ink">{label}</p>
              <p className="text-[11.5px] text-muted">{sub}</p>
            </div>
            <Toggle
              checked={enabled}
              onChange={(v) => {
                const updated = { ...notifs, [key]: v };
                patch.mutate(updated);
              }}
              size="md"
              trackOn="#159662"
              trackOff="#D8DFEA"
            />
          </div>
        );
      })}
    </div>
  );
}

// ── Main Settings Page ────────────────────────────────────────────────────────

interface SettingsPageProps {
  aiName: string;
  onAiNameChange: (name: string) => void;
}

export function SettingsPage({ aiName, onAiNameChange }: SettingsPageProps) {
  const [tab, setTab] = useState<SettingsTab>('agent');

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 flex-wrap">
          <h1
            className="text-[22px] font-bold text-ink tracking-[-0.01em]"
            style={{ fontFamily: '"Plus Jakarta Sans", system-ui' }}
          >
            Settings
          </h1>
          <ManagedPill />
        </div>
        <p className="text-[13px] text-muted mt-1">
          Team phone & availability are yours. Brain, compliance, and sequences are managed.
        </p>
      </div>

      {/* Tab bar */}
      <div
        className="flex border-b-2 mb-6"
        style={{ borderColor: '#F0F3F8' }}
      >
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className="pb-2 pt-1 px-4 text-[13px] font-medium relative transition-colors"
            style={{ color: tab === key ? '#0D1729' : '#64748B' }}
          >
            {label}
            {tab === key && (
              <span
                className="absolute bottom-[-2px] left-0 right-0 h-0.5 rounded-full"
                style={{ background: '#0D1729' }}
              />
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'agent' && <AgentTab aiName={aiName} onAiNameChange={onAiNameChange} />}
      {tab === 'team' && <TeamTab />}
      {tab === 'compliance' && <ComplianceTab />}
      {tab === 'integrations' && <IntegrationsTab />}
      {tab === 'tags' && <TagsTab />}
      {tab === 'notifications' && <NotificationsTab />}
    </div>
  );
}
